import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Truck, Spinner, Warning, CheckCircle, UploadSimple, X, CaretDown, CaretUp, ArrowsClockwise } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { useAuth } from '@/auth/AuthContext';
import { shizaiApi, yen } from './shizaiApi';

const CONF_BADGE = {
  high: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  medium: 'bg-amber-50 text-amber-700 border-amber-200',
  low: 'bg-rose-50 text-rose-700 border-rose-200',
};
const CONF_LABEL = { high: '自動一致', medium: '要確認', low: '低一致' };

/**
 * TECHS実績取込 — 入荷確認モーダル内のオプションステップ。
 * TECHS-NOA出力Excelを取込み、送料行(99ZZZ00000)をバッチ単位で数量按分して
 * 各明細へ非表示の配賦メタデータとして保存し、実際購入金額をプリフィルする。
 */
export default function TechsImportSection({ req, onApplied, hasAllocation }) {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [enabled, setEnabled] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [recalcing, setRecalcing] = useState(false);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [ack, setAck] = useState(false);
  const [applying, setApplying] = useState(false);
  const [showDebug, setShowDebug] = useState(false);

  const recalc = async () => {
    setRecalcing(true);
    try {
      const res = await shizaiApi.techsRecalcAllocation(req.id, {});
      toast.success(`送料配賦を品目均等按分で再計算しました — ${res.recalculated_line_count}件`);
      onApplied?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '再計算に失敗しました');
    } finally { setRecalcing(false); }
  };

  useEffect(() => {
    shizaiApi.techsAllocationConfig().then((c) => setEnabled(!!c.enabled)).catch(() => setEnabled(true));
  }, []);

  const toggleFlag = async (v) => {
    try {
      await shizaiApi.setTechsAllocationConfig(v);
      setEnabled(v);
      toast.success(v ? 'TECHS送料配賦を有効にしました' : 'TECHS送料配賦を無効にしました');
    } catch (e) {
      toast.error(e?.response?.data?.detail || '設定の変更に失敗しました');
    }
  };

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    setPreview(null);
    try {
      const p = await shizaiApi.techsImportPreview(req.id, file);
      setPreview(p);
      const init = {};
      (p.batches || []).forEach((b) => b.items.forEach((it) => {
        init[it.row_index] = it.match ? String(it.match.line_index) : '';
      }));
      setMapping(init);
      setAck(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'TECHS出力の解析に失敗しました');
    } finally { setUploading(false); }
  };

  const apply = async () => {
    setApplying(true);
    try {
      const entries = Object.entries(mapping).map(([ri, li]) => ({
        row_index: Number(ri), line_index: li === '' ? null : Number(li),
      }));
      const res = await shizaiApi.techsImportApply(req.id, {
        session_id: preview.session_id, mapping: entries,
      });
      const parts = [`送料配賦を適用しました — ${res.allocated_line_count}件`];
      if (res.recalculated_line_count) parts.push(`再計算 ${res.recalculated_line_count}件`);
      toast.success(parts.join(' / '), {
        description: `バッチ ${res.batch_count}件・検出送料 ${yen(res.total_shipping_detected)}`,
      });
      setPreview(null);
      onApplied?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '送料配賦の適用に失敗しました');
    } finally { setApplying(false); }
  };

  if (enabled === null) return null;
  if (!enabled && !isAdmin) return null;

  const usedLines = new Set(Object.values(mapping).filter((v) => v !== ''));
  const dupAssigned = Object.values(mapping).filter((v) => v !== '').length !== usedLines.size;

  return (
    <div className="rounded-lg border border-violet-200 bg-violet-50/40 p-3" data-testid="techs-import-section">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="text-xs font-bold text-violet-800 flex items-center gap-1.5">
          <Truck size={14} weight="duotone" /> TECHS実績取込（任意）
        </p>
        {isAdmin && (
          <label className="flex items-center gap-1.5 text-[10px] text-zinc-500 cursor-pointer">
            送料配賦
            <Switch checked={enabled} onCheckedChange={toggleFlag} className="scale-75"
              data-testid="techs-alloc-flag-toggle" />
          </label>
        )}
      </div>
      {!enabled ? (
        <p className="mt-1 text-[11px] text-zinc-500">TECHS送料配賦機能は無効化されています</p>
      ) : !preview ? (
        <>
          <p className="mt-1 text-[11px] text-violet-700/70">
            TECHS-NOAの発注実績Excelを取込むと、実際購入金額が自動入力され、送料行（送料）は同一バッチ内の品目数で均等に按分されます（品目均等按分）
          </p>
          <div className="mt-2 flex items-center gap-3 flex-wrap">
            <label className="flex items-center gap-2 rounded-md border border-dashed border-violet-300 bg-white px-3 py-2 text-xs text-violet-600 cursor-pointer hover:bg-violet-50 transition-colors w-fit">
              {uploading ? <Spinner size={14} className="animate-spin" /> : <UploadSimple size={14} />}
              {uploading ? '解析中...' : 'TECHS出力Excelを選択（.xlsx）'}
              <input type="file" accept=".xlsx,.xlsm" className="hidden" disabled={uploading}
                onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ''; }}
                data-testid="techs-import-file" />
            </label>
            {hasAllocation && (
              <button type="button" onClick={recalc} disabled={recalcing}
                className="inline-flex items-center gap-1 text-[11px] text-violet-600 hover:text-violet-800 underline underline-offset-2 disabled:opacity-50"
                data-testid="techs-recalc-btn"
                title="送料は数量ではなく、同一バッチ内の品目数で均等に分配されています。">
                {recalcing ? <Spinner size={11} className="animate-spin" /> : <ArrowsClockwise size={11} />}
                送料配賦を再計算（品目均等按分）
              </button>
            )}
          </div>
        </>
      ) : (
        <div className="mt-2 space-y-2.5" data-testid="techs-import-preview">
          <div className="flex items-center gap-2 flex-wrap text-[10px]">
            <span className="font-mono text-zinc-500 max-w-[180px] truncate"><span data-no-translate>{preview.file_name}</span></span>
            <span className="px-1.5 py-0.5 rounded bg-white border border-violet-200 text-violet-700 font-semibold">
              バッチ {preview.batch_count}件
            </span>
            <span className="px-1.5 py-0.5 rounded bg-white border border-violet-200 text-violet-700 font-semibold">
              検出送料 {yen(preview.total_shipping_detected)}
            </span>
            <span className="px-1.5 py-0.5 rounded bg-white border border-zinc-200 text-zinc-600">
              TECHS出力番号 <span className="font-mono">{preview.techs_purchase_output_no}</span>
            </span>
            <button type="button" onClick={() => setPreview(null)}
              className="ml-auto text-zinc-400 hover:text-rose-600" data-testid="techs-import-clear">
              <X size={13} weight="bold" />
            </button>
          </div>

          {(preview.warnings || []).length > 0 && (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-2.5 py-1.5 space-y-0.5"
              data-testid="techs-import-warnings">
              {preview.warnings.map((w, i) => (
                <p key={i} className="text-[10px] text-amber-800 flex items-start gap-1">
                  <Warning size={11} weight="fill" className="shrink-0 mt-[1px]" /> {w}
                </p>
              ))}
            </div>
          )}

          {/* 品目行 → 依頼明細 の割当テーブル（送料行は品目として登録されない） */}
          <div className="rounded-md border border-zinc-200 bg-white overflow-hidden">
            {preview.batches.map((b) => (
              <div key={b.batch_id}>
                {b.items.map((it) => (
                  <div key={it.row_index}
                    className="flex items-center gap-2 px-2.5 py-1.5 border-b border-zinc-200 flex-wrap"
                    data-testid={`techs-row-${it.row_index}`}>
                    <div className="min-w-0 flex-1">
                      <p className="text-[11px] font-medium text-zinc-800 truncate">{it.name}</p>
                      <p className="text-[10px] text-zinc-400 tabular-nums">
                        {it.qty}個 · {yen(it.amount)}
                        {it.allocation && (
                          <span className="text-violet-600 ml-1.5">+送料 {yen(it.allocation.allocated_shipping_total)}</span>
                        )}
                        {it.status === 'no_shipping_line_found' && (
                          <span className="text-amber-600 ml-1.5">送料行なし</span>
                        )}
                      </p>
                    </div>
                    {it.match && (
                      <span className={`px-1.5 py-0.5 rounded border text-[9px] font-semibold shrink-0 ${CONF_BADGE[it.match.confidence]}`}>
                        {CONF_LABEL[it.match.confidence]}
                      </span>
                    )}
                    <select value={mapping[it.row_index] ?? ''}
                      onChange={(e) => setMapping((p) => ({ ...p, [it.row_index]: e.target.value }))}
                      className="h-7 rounded border border-zinc-300 bg-white px-1.5 text-[11px] text-zinc-700 max-w-[190px]"
                      data-testid={`techs-map-${it.row_index}`}>
                      <option value="">割当なし</option>
                      {preview.request_lines.map((l) => (
                        <option key={l.index} value={l.index}>
                          {l.index + 1}. {l.name}（{l.qty}{l.uom || '個'}）
                        </option>
                      ))}
                    </select>
                  </div>
                ))}
                {b.shipping && (
                  <div className="flex items-center gap-2 px-2.5 py-1 bg-violet-50/60 border-b border-violet-100"
                    data-testid={`techs-shipping-row-${b.batch_index}`}>
                    <Truck size={11} className="text-violet-500 shrink-0" />
                    <p className="text-[10px] text-violet-700">
                      送料行 <span className="font-mono">{b.shipping.part_no}</span> — {yen(b.shipping.amount)}
                      {b.orphan
                        ? <span className="text-amber-600 ml-1.5">孤立（配賦されません）</span>
                        : <span className="text-violet-500 ml-1.5">上の{b.items.length}品目へ品目均等按分・品目としては登録されません</span>}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>

          {isAdmin && (
            <div>
              <button type="button" onClick={() => setShowDebug((s) => !s)}
                className="text-[10px] text-zinc-400 hover:text-zinc-600 flex items-center gap-1"
                data-testid="techs-debug-toggle">
                {showDebug ? <CaretUp size={10} /> : <CaretDown size={10} />} 配賦デバッグ（管理者）
              </button>
              {showDebug && (
                <pre className="mt-1 rounded bg-zinc-950 text-zinc-300 text-[9px] p-2 max-h-40 overflow-auto"
                  data-testid="techs-debug-json">
                  {JSON.stringify(preview.batches, null, 1)}
                </pre>
              )}
            </div>
          )}

          <div className="flex items-center gap-3 flex-wrap">
            {(preview.needs_review || dupAssigned) && (
              <label className="flex items-center gap-1.5 text-[11px] text-amber-800 cursor-pointer">
                <Checkbox checked={ack} onCheckedChange={(v) => setAck(!!v)} className="h-3.5 w-3.5"
                  data-testid="techs-review-ack" />
                確認が必要な項目を確認しました
              </label>
            )}
            <Button size="sm" onClick={apply}
              disabled={applying || dupAssigned || ((preview.needs_review) && !ack)}
              className="bg-teal-700 hover:bg-teal-800 h-8 text-xs"
              data-testid="techs-import-apply">
              {applying ? <Spinner size={13} className="animate-spin mr-1" /> : <CheckCircle size={13} className="mr-1" />}
              配賦を適用してプリフィル
            </Button>
            {dupAssigned && <span className="text-[10px] text-rose-600">同じ明細が複数行に割当てられています</span>}
          </div>
        </div>
      )}
    </div>
  );
}
